/**
 * Reports uncaught errors, unhandled promise rejections and errors caught by ErrorBoundary to
 * REST\Controller\ReportingController.
 * Only active on page views sampled for browser reporting (VXConfig.jsErrorReportingUrl is set).
 *
 * Also keeps a failed Vite CSS preload from rejecting the dynamic import. That listener always runs.
 *
 * Imported for its side effect as the first import of an entry point, so it is listening before the app runs.
 */

const MAX_REPORTS_PER_PAGE = 10;
const MAX_MESSAGE_LENGTH = 500;
const STACK_FRAME_LOCATION = /((?:https?|blob):[^\s()]+?):(\d+):(\d+)/;

const reportedKeys = new Set();

function sendReport(url, body) {
	const report = [
		{
			type: 'js-error',
			url: window.location.href,
			user_agent: navigator.userAgent,
			body,
		},
	];
	// text/plain keeps sendBeacon free of content-type restrictions; the endpoint does not depend on it
	const blob = new Blob([JSON.stringify(report)], { type: 'text/plain' });

	if (!navigator.sendBeacon || !navigator.sendBeacon(url, blob)) {
		fetch(url, { method: 'POST', body: blob, keepalive: true }).catch(() => {});
	}
}

function getLocationFromStack(stack) {
	const match = typeof stack === 'string' ? STACK_FRAME_LOCATION.exec(stack) : null;

	return match ? { sourceFile: match[1], lineNumber: Number(match[2]), columnNumber: Number(match[3]) } : {};
}

function logReportToConsole(body) {
	try {
		if (typeof console !== 'undefined' && console.error) {
			console.error('[VX js-error]', body.message, body);
		}
	} catch {
		// never let reporting cause errors of its own
	}
}

function report(url, body) {
	const key = [body.message, body.sourceFile, body.lineNumber, body.columnNumber].join('|');
	if (reportedKeys.has(key) || reportedKeys.size >= MAX_REPORTS_PER_PAGE) {
		return;
	}

	reportedKeys.add(key);
	logReportToConsole(body);
	sendReport(url, body);
}

function createErrorHandler(url) {
	return function reportUncaughtError(event) {
		try {
			// "Script error." without location: a cross-origin script, the browser hides everything else
			if (!event.filename && !event.error) {
				return;
			}

			const error = event.error instanceof Error ? event.error : null;
			report(url, {
				trigger: 'error',
				message: String(event.message).slice(0, MAX_MESSAGE_LENGTH),
				errorType: error ? error.name : undefined,
				sourceFile: event.filename || undefined,
				lineNumber: event.lineno || undefined,
				columnNumber: event.colno || undefined,
				stack: error ? error.stack : undefined,
			});
		} catch {
			// never let reporting cause errors of its own
		}
	};
}

function createRejectionHandler(url) {
	return function reportUnhandledRejection(event) {
		try {
			const reason = event.reason;
			const error = reason instanceof Error ? reason : null;
			report(url, {
				trigger: 'unhandledrejection',
				message: String(error ? error.message : reason).slice(0, MAX_MESSAGE_LENGTH),
				errorType: error ? error.name : undefined,
				stack: error ? error.stack : undefined,
				...getLocationFromStack(error ? error.stack : null),
			});
		} catch {
			// never let reporting cause errors of its own
		}
	};
}

// Vite rejects the whole dynamic import when its CSS <link> errors. The chunk's JS still runs;
// only that stylesheet is missing. A missing JS chunk is a different error and still fails.
window.addEventListener('vite:preloadError', (event) => {
	const message = String(event.payload?.message ?? '');
	if (message.startsWith('Unable to preload CSS')) {
		event.preventDefault();
	}
});

const reportingUrl = window.VXConfig?.jsErrorReportingUrl;
if (reportingUrl) {
	window.addEventListener('error', createErrorHandler(reportingUrl));
	window.addEventListener('unhandledrejection', createRejectionHandler(reportingUrl));
}

/**
 * Reports an error that was caught, e.g. by an ErrorBoundary, and so never reaches the window listeners.
 *
 * @param {*} error
 * @param {string} [componentStack] React's component stack from componentDidCatch
 */
export function reportCaughtError(error, componentStack) {
	if (!reportingUrl) {
		return;
	}

	try {
		const caught = error instanceof Error ? error : null;
		report(reportingUrl, {
			trigger: 'boundary',
			message: String(caught ? caught.message : error).slice(0, MAX_MESSAGE_LENGTH),
			errorType: caught ? caught.name : undefined,
			stack: caught ? caught.stack : undefined,
			componentStack: typeof componentStack === 'string' ? componentStack : undefined,
			...getLocationFromStack(caught ? caught.stack : null),
		});
	} catch {
		// never let reporting cause errors of its own
	}
}
